import React from 'react';
import { Check, X } from 'lucide-react';
import { UseFormRegister, FieldError } from 'react-hook-form';
import { FlavourFormData } from '../../types';

interface PackageNameInputProps {
  register: UseFormRegister<FlavourFormData>;
  error?: FieldError;
  packageNameStatus: 'idle' | 'checking' | 'available' | 'taken' | 'invalid';
  packageNameMessage: string | null;
  disabled?: boolean;
  platform?: 'ios' | 'android' | 'both';
}

const PackageNameInput: React.FC<PackageNameInputProps> = ({
  register,
  error,
  packageNameStatus,
  packageNameMessage,
  disabled = false,
  platform = 'both',
}) => {
  const renderStatus = () => {
    if (disabled) {
      return <Check className="h-4 w-4 text-gray-400" />;
    }
    
    switch (packageNameStatus) {
      case 'checking':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>;
      case 'available':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'taken':
      case 'invalid':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <div>
      <label className={`block text-sm font-medium mb-1 ${disabled ? 'text-gray-500' : 'text-gray-700'}`}>
        Package Name * {disabled && <span className="text-xs text-gray-400">(Cannot be changed)</span>}
      </label>
      <div className="relative">
        <input
          {...register('packageName', { 
            required: 'Package name is required',
            pattern: {
              value: /\./,
              message: 'Package name must contain at least one dot (.)'
            }
          })}
          type="text"
          disabled={disabled}
          className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-primary focus:border-primary ${
            disabled 
              ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed' 
              : 'border-gray-300'
          }`}
          placeholder="com.company.appname"
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          {renderStatus()}
        </div>
      </div>
      {!disabled && (
        <p className="mt-1 text-xs text-gray-500">
          Must contain at least one dot (e.g., com.company.appname)
        </p>
      )}
      {disabled && (
        <p className="mt-1 text-sm text-gray-500">
          Package name is locked and cannot be changed
        </p>
      )}
      {!disabled && packageNameMessage && (
        <p className={`mt-1 text-sm ${
          packageNameStatus === 'available' ? 'text-green-600' : 
          packageNameStatus === 'taken' || packageNameStatus === 'invalid' ? 'text-red-600' : 'text-gray-500'
        }`}>
          {packageNameMessage}
        </p>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error.message}</p>
      )}
    </div>
  );
};

export default PackageNameInput; 